/**
 * Grouping of the Agent map's points (#220, INV-PH-97): dozens of «где …?» points of one park lie on
 * top of each other at a small scale. Own greedy grouping by SCREEN distance, no plugin:
 * `@2gis/mapgl-clusterer` is a separate package with its own marker styling and tap handling, while
 * the Agent's map needs only a disc with a number and its points already arrive as an array.
 *
 * Only the map's own points are grouped (the caller decides which): never the visitor's place, never
 * a route's start / finish / numbered stops. A group of one is the point itself.
 */
import type { Map as MapGLMap } from '@2gis/mapgl/types';
import { fitToRoute } from '../map/camera';

/** Fewer points than this are never grouped: a handful of discs reads as it is. */
export const CLUSTER_MIN_POINTS = 8;
/** Two points closer than this on the screen (px) fall into one group. */
export const CLUSTER_RADIUS_PX = 44;
/** From this zoom on points stay apart; the camera never goes closer than 17 by itself, so coincident points would stay a group forever. */
export const CLUSTER_MAX_ZOOM = 17;

export type ScreenPoint = { x: number; y: number };

/** Groups of indices into `points`, in input order; every point is in exactly one group. */
export function groupByScreen(points: readonly ScreenPoint[], radius = CLUSTER_RADIUS_PX, minPoints = CLUSTER_MIN_POINTS): number[][] {
  const single = () => points.map((_, index) => [index]);
  if (points.length < minPoints) return single();
  const groups: { seed: ScreenPoint; members: number[] }[] = [];
  points.forEach((point, index) => {
    const near = groups.find((group) => Math.hypot(group.seed.x - point.x, group.seed.y - point.y) <= radius);
    if (near) near.members.push(index);
    else groups.push({ seed: point, members: [index] });
  });
  // nothing overlaps: leave the points alone
  return groups.some((group) => group.members.length > 1) ? groups.map((group) => group.members) : single();
}

/** The groups for the map's current zoom. *minPoints*: a route's numbered stops group from two on (`STOPS_MIN_POINTS`). */
export function groupOnMap<T extends { lat: number; lon: number }>(map: Pick<MapGLMap, 'project' | 'getZoom'>, items: readonly T[], minPoints = CLUSTER_MIN_POINTS): T[][] {
  if (map.getZoom() >= CLUSTER_MAX_ZOOM) return items.map((item) => [item]);
  const screen = items.map((item) => {
    const [x = 0, y = 0] = map.project([item.lon, item.lat]);
    return { x, y };
  });
  return groupByScreen(screen, CLUSTER_RADIUS_PX, minPoints).map((members) => members.map((index) => items[index]!));
}

export function clusterLabel(count: number): string {
  const rest = count % 100;
  const form = rest >= 11 && rest <= 14 ? 'точек' : count % 10 === 1 ? 'точка' : count % 10 >= 2 && count % 10 <= 4 ? 'точки' : 'точек';
  return `${count} ${form}, приблизить`;
}

/** The disc: a number, no icon. A real button, so a keyboard reaches it. */
export function clusterHtml(count: number): string {
  return `<button type="button" class="agent-route-marker agent-route-marker--cluster" aria-label="${clusterLabel(count)}">${count}</button>`;
}

/** A plan's numbered stops group as soon as two lie on top of each other: a walk of ~800 m beside a 40 km ride
 *  put «1 2 3 4» into one blot (live dev 12.10.2026, a trip through the whole plan). */
export const STOPS_MIN_POINTS = 2;

/** «1–4», «2, 5» — the stop numbers a group holds, in order; runs of consecutive numbers as a range. */
export function stopsLabel(numbers: readonly number[]): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const runs: string[] = [];
  for (let start = 0; start < sorted.length;) {
    let end = start;
    while (end + 1 < sorted.length && sorted[end + 1] === sorted[end]! + 1) end += 1;
    runs.push(end > start ? `${sorted[start]}–${sorted[end]}` : String(sorted[start]));
    start = end + 1;
  }
  return runs.join(', ');
}

/** A group of a route's stops: their numbers, not a count — «4» would read as the fourth stop. */
export function stopsClusterHtml(numbers: readonly number[]): string {
  const label = stopsLabel(numbers);
  return `<button type="button" class="agent-route-marker agent-route-marker--cluster agent-route-marker--stops" aria-label="Остановки ${label}, приблизить">${label}</button>`;
}

export function clusterCentre(members: readonly { lat: number; lon: number }[]): [number, number] {
  const lat = members.reduce((sum, item) => sum + item.lat, 0) / members.length;
  const lon = members.reduce((sum, item) => sum + item.lon, 0) / members.length;
  return [lon, lat];
}

/** A tap on a group: the camera goes to the group's bounds, which opens it. */
export function zoomToCluster(map: MapGLMap, members: readonly { lat: number; lon: number }[], padding: Parameters<typeof fitToRoute>[2]): void {
  fitToRoute(map, members.map((item) => [item.lon, item.lat] as [number, number]), padding, { maxZoom: CLUSTER_MAX_ZOOM, durationMs: 350 });
}
